import React from 'react';
import toast, { type Toast as HotToast } from 'react-hot-toast';
import { formatSpokenDateParts } from '../../utils/dateFormat';

type NoticeVariant = 'not_yet_open' | 'closed';

function DismissChip({ onDismiss }: { onDismiss: () => void }) {
  return (
    <button
      type="button"
      onClick={onDismiss}
      className="absolute right-3 top-3 z-[3] flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/50 text-white/55 backdrop-blur-md transition hover:border-amber-300/40 hover:bg-white/10 hover:text-white"
      aria-label="Dismiss"
    >
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

function Sparkle({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 1l1.6 7.1L21 10l-7.4 2.4L12 19l-1.6-6.6L3 10l7.4-1.9L12 1z" />
    </svg>
  );
}

function BookingNoticeInner({
  t,
  variant,
  bookingStartIso,
  eventTitle,
}: {
  t: HotToast;
  variant: NoticeVariant;
  bookingStartIso?: string | null;
  eventTitle?: string;
}) {
  const parts = bookingStartIso ? formatSpokenDateParts(bookingStartIso) : { dateLine: '', timeLine: '', full: '' };
  const isSoon = variant === 'not_yet_open';

  return (
    <div
      className={`pointer-events-auto relative max-w-[min(440px,calc(100vw-1.5rem))] cursor-default ${
        t.visible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-5 scale-[0.97] opacity-0'
      } transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]`}
      role="status"
    >
      {/* Gradient border frame */}
      <div
        className={`rounded-[1.35rem] p-[1.5px] shadow-2xl ${
          isSoon
            ? 'bg-gradient-to-br from-amber-200/90 via-primary to-amber-400/80 shadow-[0_0_48px_-8px_rgba(251,191,36,0.45)]'
            : 'bg-gradient-to-br from-slate-400/50 via-white/25 to-slate-500/40'
        }`}
      >
        <div
          className={`relative overflow-hidden rounded-[1.28rem] px-6 pb-7 pt-8 pr-14 backdrop-blur-2xl ${
            isSoon
              ? 'border border-amber-400/20 bg-[#050402]/[0.97]'
              : 'border border-white/10 bg-[#08080a]/[0.98]'
          }`}
        >
          <DismissChip onDismiss={() => toast.dismiss(t.id)} />

          {/* Moving shine */}
          <div
            className="pointer-events-none absolute inset-0 overflow-hidden rounded-[1.2rem]"
            aria-hidden
          >
            <div
              className={`absolute -left-1/2 top-0 h-full w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent animate-toast-shine ${
                isSoon ? '' : 'via-white/12'
              }`}
            />
          </div>

          {/* Soft orbs */}
          <div
            className={`pointer-events-none absolute -left-16 -top-20 h-48 w-48 rounded-full blur-3xl ${
              isSoon ? 'bg-amber-400/35 animate-success-glow' : 'bg-slate-500/20'
            }`}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-16 -right-12 h-44 w-44 rounded-full bg-primary/30 blur-[72px] opacity-70 animate-toast-glow-pulse"
            style={{ animationDelay: '0.6s' }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-500/[0.08] via-transparent to-primary/[0.12]"
            aria-hidden
          />
          <div
            className={`pointer-events-none absolute inset-x-8 top-0 h-[2px] bg-gradient-to-r from-transparent to-transparent ${
              isSoon ? 'via-amber-200/90' : 'via-white/50'
            }`}
            aria-hidden
          />

          <div className="relative z-[1]">
            {isSoon ? (
              <>
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-amber-400/35 bg-gradient-to-br from-amber-500/25 to-primary/20 text-lg shadow-[0_0_24px_rgba(251,191,36,0.35)]">
                    🎟
                  </span>
                  <div>
                    <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.38em] text-amber-200/95 drop-shadow-[0_0_14px_rgba(251,191,36,0.5)]">
                      Tickets go live
                      <Sparkle className="h-3 w-3 text-amber-300/90 animate-toast-sparkle" />
                    </p>
                    {eventTitle ? (
                      <p className="mt-1 max-w-[280px] truncate text-xs font-medium text-white/50">{eventTitle}</p>
                    ) : null}
                  </div>
                </div>

                {parts.dateLine ? (
                  <div className="mt-2 space-y-3">
                    <p className="font-display text-[1.65rem] font-semibold leading-[1.15] tracking-tight text-white sm:text-3xl">
                      <span className="bg-gradient-to-br from-amber-50 via-white to-amber-100 bg-clip-text text-transparent drop-shadow-[0_0_40px_rgba(255,255,255,0.2)]">
                        {parts.dateLine}
                      </span>
                    </p>
                    {parts.timeLine ? (
                      <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/35 bg-gradient-to-r from-amber-500/15 to-primary/10 px-4 py-2 text-sm font-semibold text-amber-50 shadow-[0_0_20px_rgba(251,191,36,0.15)] backdrop-blur-sm">
                        <svg
                          className="h-4 w-4 shrink-0 text-amber-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="tracking-wide">{parts.timeLine}</span>
                      </div>
                    ) : null}
                    <p className="pt-2 text-sm leading-relaxed text-white/55">
                      Sales aren&apos;t open yet - come back at the time above to choose tiers and checkout.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-white/65">
                    Sales aren&apos;t open yet. Check back soon or follow updates from the organiser.
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.32em] text-white/45">
                  Ticket sales
                  <Sparkle className="h-2.5 w-2.5 text-white/30" />
                </div>
                <p className="text-lg font-medium leading-snug text-white/90">
                  Ticket sales for this experience have ended.
                </p>
                <p className="mt-3 text-sm text-white/45">Explore other dates and cities on Discover.</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BookingBlockedInner({
  t,
  bookingStartIso,
}: {
  t: HotToast;
  bookingStartIso?: string | null;
}) {
  const parts = bookingStartIso ? formatSpokenDateParts(bookingStartIso) : { dateLine: '', timeLine: '', full: '' };

  return (
    <div
      className={`pointer-events-auto relative max-w-[min(440px,calc(100vw-1.5rem))] cursor-default transition-all duration-300 ease-out ${
        t.visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
      role="alert"
    >
      <div className="rounded-[1.35rem] bg-gradient-to-br from-primary via-red-600 to-primary p-[1.5px] shadow-[0_0_40px_rgba(229,57,53,0.45)] animate-toast-glow-pulse">
        <div className="relative overflow-hidden rounded-[1.28rem] border border-primary/30 bg-[#120505]/[0.98] px-6 pb-7 pt-8 pr-14 backdrop-blur-2xl">
          <DismissChip onDismiss={() => toast.dismiss(t.id)} />

          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[1.2rem]" aria-hidden>
            <div className="absolute -left-1/2 top-0 h-full w-1/2 animate-toast-shine bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          </div>
          <div
            className="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full bg-primary/40 blur-3xl opacity-60"
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-x-6 top-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-90" aria-hidden />

          <div className="relative z-[1]">
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-primary drop-shadow-[0_0_12px_rgba(229,57,53,0.6)]">
              Checkout locked
            </p>
            <p className="mt-3 text-base font-medium text-white/92">
              Payment opens when ticket sales are live.
            </p>
            {parts.dateLine ? (
              <div className="mt-5 space-y-2">
                <p className="font-display text-xl font-semibold leading-tight text-white sm:text-2xl">
                  <span className="bg-gradient-to-r from-amber-100 via-white to-primary bg-clip-text text-transparent">
                    {parts.dateLine}
                  </span>
                </p>
                {parts.timeLine ? (
                  <div className="inline-flex items-center gap-2 rounded-full border border-primary/35 bg-primary/10 px-3.5 py-1.5 text-sm font-semibold text-white/90">
                    <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {parts.timeLine}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Rich notice when sales aren’t open or have ended (event detail, checkout). */
export function showBookingWindowNoticeToast(
  variant: NoticeVariant,
  bookingStartIso?: string | null,
  eventTitle?: string
): void {
  toast.custom(
    (t) => (
      <BookingNoticeInner t={t} variant={variant} bookingStartIso={bookingStartIso} eventTitle={eventTitle} />
    ),
    {
      duration: variant === 'not_yet_open' ? 10_500 : 7000,
      position: 'top-center',
    }
  );
}

/** Shown when user tries to pay while sales are still closed. */
export function showCheckoutBlockedToast(bookingStartIso?: string | null): void {
  toast.custom((t) => <BookingBlockedInner t={t} bookingStartIso={bookingStartIso} />, {
    duration: 8000,
    position: 'top-center',
  });
}
